/**
 * Verder op je telefoon: de afspraak tussen twee schermen.
 *
 * De klant zit achter een laptop en wil een foto uit zijn telefoon gebruiken.
 * Die foto staat daar in volle resolutie; via Instagram of een gedeelde map
 * krijg je hooguit een verkleinde kopie terug. Dus zetten we de telefoon zelf
 * aan het uploaden en laten we de laptop het resultaat oppikken.
 *
 * De laptop verzint een sessienummer, toont dat als QR, en kijkt daarna
 * regelmatig of er iets binnen is. De telefoon opent dat linkje, uploadt langs
 * precies dezelfde weg als een gewone upload - dus byte-identiek, met
 * voortgang, tot 500 MB - en meldt het resultaat op datzelfde sessienummer.
 *
 * Er komt geen tabel aan te pas. De ontmoetingsplek is een regel in de
 * bestaande Setting-tabel die vanzelf verloopt. Dat scheelt een migratie die
 * met de hand op productie gedraaid moet worden, en er valt niets te bewaren:
 * zodra de laptop het bestand heeft opgepikt is de afspraak klaar.
 */

/** Zonder klinkers en zonder tekens die op elkaar lijken: geen 0/O, geen 1/l/I. */
const ALPHABET = '23456789abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ';

/** 22 tekens uit dit alfabet is ruim 128 bits. */
export const SESSION_ID_LENGTH = 22;

/** Hoelang een afspraak geldig blijft. Ruim genoeg om een foto te zoeken. */
export const HANDOFF_TTL_MS = 30 * 60 * 1000;

/** Meer dan dit aantal bestanden per sessie nemen we niet aan. */
export const HANDOFF_MAX_FILES = 10;

export function createSessionId(): string {
  const bytes = new Uint8Array(SESSION_ID_LENGTH);
  crypto.getRandomValues(bytes);
  let out = '';
  for (const byte of bytes) out += ALPHABET[byte % ALPHABET.length];
  return out;
}

/**
 * Streng, want dit nummer gaat rechtstreeks in een databasesleutel. Alles wat
 * er niet exact uitziet als een eigen sessienummer wordt geweigerd.
 */
export function isValidSessionId(value: unknown): value is string {
  if (typeof value !== 'string' || value.length !== SESSION_ID_LENGTH) return false;
  for (const char of value) if (!ALPHABET.includes(char)) return false;
  return true;
}

export function handoffSettingKey(sessionId: string): string {
  return `handoff:${sessionId}`;
}

export type HandoffFile = {
  /** Id van de Upload-regel, zodat de laptop hem net zo gebruikt als een eigen upload. */
  id: string;
  url: string;
  fileName: string;
  fileSize: number;
  /** Milliseconden sinds epoch. */
  at: number;
};

export type HandoffRecord = {
  files: HandoffFile[];
  createdAt: number;
};

export function isExpired(record: { createdAt?: unknown }, now: number): boolean {
  const createdAt = Number(record?.createdAt);
  if (!Number.isFinite(createdAt)) return true;
  return now - createdAt > HANDOFF_TTL_MS;
}

/** Alleen wat de laptop nodig heeft, en niets wat de klant kan verzinnen. */
export function sanitizeHandoffFile(input: unknown, now: number): HandoffFile | null {
  if (!input || typeof input !== 'object') return null;
  const source = input as Record<string, unknown>;
  const id = typeof source.id === 'string' ? source.id.slice(0, 64) : '';
  const url = typeof source.url === 'string' ? source.url : '';
  const fileName = typeof source.fileName === 'string' ? source.fileName.slice(0, 200) : '';
  const fileSize = Number(source.fileSize);

  if (!id || !url || !fileName) return null;
  if (!Number.isFinite(fileSize) || fileSize <= 0) return null;

  return { id, url, fileName, fileSize, at: now };
}
